import type { GeneratedSubscription } from "@aws-amplify/api-graphql";
import {
  HistoryValidationGroupStatus,
  HistoryValidationStatus,
  OnCreateAppointmentFilterPresetSubscription,
  OnCreateAppointmentFilterPresetSubscriptionVariables,
  OnCreateAppointmentRecordPatientCaseSubscription,
  OnCreateAppointmentRecordPatientCaseSubscriptionVariables,
  OnCreateCoverLetterRecapSubscription,
  OnCreateCoverLetterRecapSubscriptionVariables,
  OnCreateDocumentSubscriptionVariables,
  OnCreateHistoryValidationSubscriptionVariables,
  OnCreateIntroParagraphSubscription,
  OnCreateIntroParagraphSubscriptionVariables,
  OnCreateNotesSubscription,
  OnCreateNotesSubscriptionVariables,
  OnCreateRecordBriefingSegmentSubscription,
  OnCreateRecordBriefingSegmentSubscriptionVariables,
  OnCreateUploadedFileSubscription,
  OnCreateUploadedFileSubscriptionVariables,
  OnCreateValidationCheckSubscriptionVariables,
  OnDeleteAppointmentFilterPresetSubscription,
  OnDeleteAppointmentFilterPresetSubscriptionVariables,
  OnDeleteAppointmentRecordPatientCaseSubscription,
  OnDeleteAppointmentRecordPatientCaseSubscriptionVariables,
  OnDeleteCoverLetterRecapSubscription,
  OnDeleteCoverLetterRecapSubscriptionVariables,
  OnDeleteIntroParagraphSubscription,
  OnDeleteIntroParagraphSubscriptionVariables,
  OnDeleteRecordBriefingSegmentSubscription,
  OnDeleteRecordBriefingSegmentSubscriptionVariables,
  OnDeleteUploadedFileSubscription,
  OnDeleteUploadedFileSubscriptionVariables,
  OnUpdateAppointmentRecordPatientCaseSubscription,
  OnUpdateAppointmentRecordPatientCaseSubscriptionVariables,
  OnUpdateAppointmentRecordSubscription,
  OnUpdateAppointmentRecordSubscriptionVariables,
  OnUpdateAssessmentSubscription,
  OnUpdateAssessmentSubscriptionVariables,
  OnUpdateCoverLetterRecapSubscription,
  OnUpdateCoverLetterRecapSubscriptionVariables,
  OnUpdateDoctorSubscription,
  OnUpdateDoctorSubscriptionVariables,
  OnUpdateDocumentSubscriptionVariables,
  OnUpdateHistoryValidationSubscriptionVariables,
  OnUpdateIntroParagraphSubscription,
  OnUpdateIntroParagraphSubscriptionVariables,
  OnUpdateNotesSubscription,
  OnUpdateNotesSubscriptionVariables,
  OnUpdatePatientCaseSubscription,
  OnUpdatePatientCaseSubscriptionVariables,
  OnUpdateRecordBriefingSegmentMappingSubscription,
  OnUpdateRecordBriefingSegmentMappingSubscriptionVariables,
  OnUpdateReviewOfMedicalRecordsInsightsSubscription,
  OnUpdateReviewOfMedicalRecordsInsightsSubscriptionVariables,
  OnUpdateUploadedFileSubscription,
  OnUpdateUploadedFileSubscriptionVariables,
  OnUpdateValidationCheckSubscriptionVariables,
  PatientInfoSection,
  ValidationIssueType,
  ValidationStatus,
} from "~/api/API";
import type { DocumentForDebug } from "~/api/graphql-custom/queries";

// --- Local result types for subscriptions whose selection set exceeds ---
// --- the generated `*Subscription` types at `maxDepth: 2` in API.ts.   ---

export type HistoryValidationOptimizedResult = {
  __typename: "HistoryValidation";
  id: string;
  appointmentRecordID: string;
  status: HistoryValidationStatus;
  validatedAt?: string | null;
  errorMessage?: string | null;
  groups: Array<{
    __typename: "HistoryValidationGroup";
    id: string;
    title: string;
    status: HistoryValidationGroupStatus;
    issues: Array<{
      __typename: "HistoryValidationIssue";
      id: string;
      title: string;
      description?: string | null;
      patientInfoSection?: PatientInfoSection | null;
      issueType?: ValidationIssueType | null;
    } | null>;
  } | null>;
  createdAt: string;
  updatedAt: string;
};

export type OnUpdateHistoryValidationOptimizedSubscription = {
  onUpdateHistoryValidation?: HistoryValidationOptimizedResult | null;
};

export type OnCreateHistoryValidationOptimizedSubscription = {
  onCreateHistoryValidation?: HistoryValidationOptimizedResult | null;
};

export type ValidationCheckOptimizedResult = {
  __typename: "ValidationCheck";
  id: string;
  validationID: string;
  checkType: string;
  inputResourceURIs: Array<string | null>;
  status: ValidationStatus;
  issues?: {
    __typename: "ModelValidationIssueConnection";
    items: Array<{
      __typename: "ValidationIssue";
      id: string;
      validationID: string;
      validationCheckID: string;
      title?: string | null;
      issue: string;
      status: ValidationStatus;
      resourceURI: string;
      issueGroup: string;
      issueType?: ValidationIssueType | null;
      createdAt: string;
      updatedAt: string;
    } | null>;
    nextToken?: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
};

export type OnUpdateValidationCheckOptimizedSubscription = {
  onUpdateValidationCheck?: ValidationCheckOptimizedResult | null;
};

export type OnCreateValidationCheckOptimizedSubscription = {
  onCreateValidationCheck?: ValidationCheckOptimizedResult | null;
};

export const onUpdateAppointmentRecordOptimized =
  `subscription OnUpdateAppointmentRecord(
    $filter: ModelSubscriptionAppointmentRecordFilterInput
  ) {
    onUpdateAppointmentRecord(filter: $filter) {
      id
      originalAppointmentID
      supplementalAppointmentIDs
      supplementalSourceAppointmentIDs
      rescheduledFromAppointmentID
      rescheduledToAppointmentID
      primaryPatientCaseID
      reportConfiguration {
        history
        physicalExamination
        assessment
      }
      type
      isProd
      doctorID
      uploadedFilesSetID
      appointmentRecordMedicalAssistantId
      appointmentRecordOnSiteOpId
      appointmentRecordReportOpId
      appointmentRecordBillingOpId
      appointmentRecordPreVisitReviewerId
      appointmentRecordHistoryOfInjuryNotesId
      appointmentRecordCurrentComplaintNotesId
      appointmentRecordHistoryOfInjuryInsightsId
      appointmentRecordReviewOfMedicalRecordsInsightsId
      appointmentRecordPhysicalExamInsightsId
      appointmentRecordPastInjuryMatrixId
      opsChecklist {
        panel
        contact_info
        all_parties
        notice_110
        cover_letter
        record_review
        history_call
        history_of_injury
        interpreter_info
        jotform
        deu_100
        final_report
        __typename
      }
      __typename
    }
  }
` as GeneratedSubscription<
    OnUpdateAppointmentRecordSubscriptionVariables,
    OnUpdateAppointmentRecordSubscription
  >;

/**
 * Lightweight subscription for staleness detection. The V2 save path
 * (services/record_briefing/save_v2/publisher.py) flips
 * Document.recordBriefingId via AppSync, so cross-user staleness is
 * detected by watching the per-Document pointer.
 */
export type OnUpdateDocumentRecordBriefingIdSubscription = {
  onUpdateDocument?: {
    id: string;
    recordBriefingId: string | null;
  } | null;
};

export const onUpdateDocumentRecordBriefingIdOptimized =
  `subscription OnUpdateDocumentRecordBriefingId(
    $filter: ModelSubscriptionDocumentFilterInput
  ) {
    onUpdateDocument(filter: $filter) {
      id
      recordBriefingId
    }
  }
` as GeneratedSubscription<
    OnUpdateDocumentSubscriptionVariables,
    OnUpdateDocumentRecordBriefingIdSubscription
  >;

// Staleness signal — fires when rebuild_cache_only rewrites the cache row without touching the FK on AppointmentRecord.
export const onUpdateReviewOfMedicalRecordsInsightsOptimized =
  `subscription OnUpdateReviewOfMedicalRecordsInsights(
    $filter: ModelSubscriptionReviewOfMedicalRecordsInsightsFilterInput
  ) {
    onUpdateReviewOfMedicalRecordsInsights(filter: $filter) {
      id
      appointmentRecordID
      __typename
    }
  }
` as GeneratedSubscription<
    OnUpdateReviewOfMedicalRecordsInsightsSubscriptionVariables,
    OnUpdateReviewOfMedicalRecordsInsightsSubscription
  >;

export const onUpdateAppointmentRecordPatientCaseOptimized =
  `subscription OnUpdateAppointmentRecordPatientCase(
    $filter: ModelSubscriptionAppointmentRecordPatientCaseFilterInput
  ) {
    onUpdateAppointmentRecordPatientCase(filter: $filter) {
      id
      appointmentRecordId
      patientCaseId
      __typename
    }
  }
` as GeneratedSubscription<
    OnUpdateAppointmentRecordPatientCaseSubscriptionVariables,
    OnUpdateAppointmentRecordPatientCaseSubscription
  >;

export const onCreateAppointmentRecordPatientCaseOptimized =
  `subscription OnCreateAppointmentRecordPatientCase(
    $filter: ModelSubscriptionAppointmentRecordPatientCaseFilterInput
  ) {
    onCreateAppointmentRecordPatientCase(filter: $filter) {
      id
      appointmentRecordId
      patientCaseId
      __typename
    }
  }
` as GeneratedSubscription<
    OnCreateAppointmentRecordPatientCaseSubscriptionVariables,
    OnCreateAppointmentRecordPatientCaseSubscription
  >;

export const onDeleteAppointmentRecordPatientCaseOptimized =
  `subscription OnDeleteAppointmentRecordPatientCase(
    $filter: ModelSubscriptionAppointmentRecordPatientCaseFilterInput
  ) {
    onDeleteAppointmentRecordPatientCase(filter: $filter) {
      id
      appointmentRecordId
      patientCaseId
      __typename
    }
  }
` as GeneratedSubscription<
    OnDeleteAppointmentRecordPatientCaseSubscriptionVariables,
    OnDeleteAppointmentRecordPatientCaseSubscription
  >;

export const onUpdateNotesOptimized = `subscription OnUpdateNotes(
    $filter: ModelSubscriptionNotesFilterInput
  ) {
    onUpdateNotes(filter: $filter) {
      id
      content
      type
      timeStamp
      appointmentrecordID
      __typename
    }
  }
` as GeneratedSubscription<
  OnUpdateNotesSubscriptionVariables,
  OnUpdateNotesSubscription
>;

export const onCreateNotesOptimized = `subscription OnCreateNotes(
    $filter: ModelSubscriptionNotesFilterInput
  ) {
    onCreateNotes(filter: $filter) {
      id
      content
      type
      timeStamp
      appointmentrecordID
      __typename
    }
  }
` as GeneratedSubscription<
  OnCreateNotesSubscriptionVariables,
  OnCreateNotesSubscription
>;

export const onUpdatePatientCaseOptimized = `subscription OnUpdatePatientCase(
    $filter: ModelSubscriptionPatientCaseFilterInput
  ) {
    onUpdatePatientCase(filter: $filter) {
      id
      patientID
      claimNumber
      __typename
    }
  }
` as GeneratedSubscription<
  OnUpdatePatientCaseSubscriptionVariables,
  OnUpdatePatientCaseSubscription
>;

export const onCreateUploadedFileOptimized = `subscription OnCreateUploadedFile(
  $filter: ModelSubscriptionUploadedFileFilterInput
) {
  onCreateUploadedFile(filter: $filter) {
    id
    key
    type
    provider
    uploadedBy
    uploadedAt
    sourceAppointmentID
    createdAt
    updatedAt
  }
}
` as GeneratedSubscription<
  OnCreateUploadedFileSubscriptionVariables,
  OnCreateUploadedFileSubscription
>;

export const onUpdateUploadedFileOptimized = `subscription OnUpdateUploadedFile(
  $filter: ModelSubscriptionUploadedFileFilterInput
) {
  onUpdateUploadedFile(filter: $filter) {
    id
    key
    type
    provider
    uploadedBy
    uploadedAt
    sourceAppointmentID
    createdAt
    updatedAt
  }
}
` as GeneratedSubscription<
  OnUpdateUploadedFileSubscriptionVariables,
  OnUpdateUploadedFileSubscription
>;

export const onDeleteUploadedFileOptimized = `subscription OnDeleteUploadedFile(
  $filter: ModelSubscriptionUploadedFileFilterInput
) {
  onDeleteUploadedFile(filter: $filter) {
    id
    key
    type
    provider
    uploadedBy
    uploadedAt
    sourceAppointmentID
    createdAt
    updatedAt
  }
}
` as GeneratedSubscription<
  OnDeleteUploadedFileSubscriptionVariables,
  OnDeleteUploadedFileSubscription
>;

export const onUpdateDoctorOptimized = `subscription OnUpdateDoctor(
  $filter: ModelSubscriptionDoctorFilterInput
) {
  onUpdateDoctor(filter: $filter) {
    id
  }
}
` as GeneratedSubscription<
  OnUpdateDoctorSubscriptionVariables,
  OnUpdateDoctorSubscription
>;

export const onCreateAppointmentFilterPresetOptimized =
  /* GraphQL */ `subscription OnCreateAppointmentFilterPreset(
  $filter: ModelSubscriptionAppointmentFilterPresetFilterInput
) {
  onCreateAppointmentFilterPreset(filter: $filter) {
    id
    name
    filters {
      type
      values
      __typename
    }
    appliesTo
    view
  }
}
` as GeneratedSubscription<
    OnCreateAppointmentFilterPresetSubscriptionVariables,
    OnCreateAppointmentFilterPresetSubscription
  >;

export const onDeleteAppointmentFilterPresetOptimized =
  /* GraphQL */ `subscription OnDeleteAppointmentFilterPreset(
  $filter: ModelSubscriptionAppointmentFilterPresetFilterInput
) {
  onDeleteAppointmentFilterPreset(filter: $filter) {
    id
  }
}
` as GeneratedSubscription<
    OnDeleteAppointmentFilterPresetSubscriptionVariables,
    OnDeleteAppointmentFilterPresetSubscription
  >;

export const onCreateCoverLetterRecapOptimized =
  `subscription OnCreateCoverLetterRecap(
  $filter: ModelSubscriptionCoverLetterRecapFilterInput
) {
  onCreateCoverLetterRecap(filter: $filter) {
    id
    uploadedFileID
    recapText
    status
    errorMessage
    generatedAt
    lastEditedAt
    lastEditedBy
    createdAt
    updatedAt
    __typename
  }
}` as GeneratedSubscription<
    OnCreateCoverLetterRecapSubscriptionVariables,
    OnCreateCoverLetterRecapSubscription
  >;

export const onUpdateCoverLetterRecapOptimized =
  `subscription OnUpdateCoverLetterRecap(
  $filter: ModelSubscriptionCoverLetterRecapFilterInput
) {
  onUpdateCoverLetterRecap(filter: $filter) {
    id
    uploadedFileID
    recapText
    status
    errorMessage
    generatedAt
    lastEditedAt
    lastEditedBy
    createdAt
    updatedAt
    __typename
  }
}` as GeneratedSubscription<
    OnUpdateCoverLetterRecapSubscriptionVariables,
    OnUpdateCoverLetterRecapSubscription
  >;

export const onDeleteCoverLetterRecapOptimized =
  `subscription OnDeleteCoverLetterRecap(
  $filter: ModelSubscriptionCoverLetterRecapFilterInput
) {
  onDeleteCoverLetterRecap(filter: $filter) {
    id
    uploadedFileID
    recapText
    status
    errorMessage
    generatedAt
    lastEditedAt
    lastEditedBy
    createdAt
    updatedAt
    __typename
  }
}` as GeneratedSubscription<
    OnDeleteCoverLetterRecapSubscriptionVariables,
    OnDeleteCoverLetterRecapSubscription
  >;

export const onCreateIntroParagraphOptimized =
  `subscription OnCreateIntroParagraph(
  $filter: ModelSubscriptionIntroParagraphFilterInput
) {
  onCreateIntroParagraph(filter: $filter) {
    id
    appointmentRecordID
    introParagraphText
    sourceRecaps {
      coverLetterRecapID
      uploadedFileID
      recapText
      generatedAt
      __typename
    }
    status
    errorMessage
    generatedAt
    lastEditedAt
    lastEditedBy
    createdAt
    updatedAt
    __typename
  }
}` as GeneratedSubscription<
    OnCreateIntroParagraphSubscriptionVariables,
    OnCreateIntroParagraphSubscription
  >;

export const onUpdateIntroParagraphOptimized =
  `subscription OnUpdateIntroParagraph(
  $filter: ModelSubscriptionIntroParagraphFilterInput
) {
  onUpdateIntroParagraph(filter: $filter) {
    id
    appointmentRecordID
    introParagraphText
    sourceRecaps {
      coverLetterRecapID
      uploadedFileID
      recapText
      generatedAt
      __typename
    }
    status
    errorMessage
    generatedAt
    lastEditedAt
    lastEditedBy
    createdAt
    updatedAt
    __typename
  }
}` as GeneratedSubscription<
    OnUpdateIntroParagraphSubscriptionVariables,
    OnUpdateIntroParagraphSubscription
  >;

export const onDeleteIntroParagraphOptimized =
  `subscription OnDeleteIntroParagraph(
  $filter: ModelSubscriptionIntroParagraphFilterInput
) {
  onDeleteIntroParagraph(filter: $filter) {
    id
    appointmentRecordID
    introParagraphText
    sourceRecaps {
      coverLetterRecapID
      uploadedFileID
      recapText
      generatedAt
      __typename
    }
    status
    errorMessage
    generatedAt
    lastEditedAt
    lastEditedBy
    createdAt
    updatedAt
    __typename
  }
}` as GeneratedSubscription<
    OnDeleteIntroParagraphSubscriptionVariables,
    OnDeleteIntroParagraphSubscription
  >;

// ========================================================================================
// Record Briefing Segment Subscriptions
// ========================================================================================

export const onCreateRecordBriefingSegmentOptimized =
  /* GraphQL */ `subscription OnCreateRecordBriefingSegment(
  $filter: ModelSubscriptionRecordBriefingSegmentFilterInput
) {
  onCreateRecordBriefingSegment(filter: $filter) {
    id
    logicalSegmentID
    title
    text
    bodyParts
    editedBy
    pdfFileName
    sourceTag
    segmentNumber
    sourcePdfS3Key
    startPageNumber
    endPageNumber
    recordType
    extractedDate
    doctorName
    processingModel
    classificationConfidence
    hasDate
    potentialDuplicateID
    needsReview
    status
    createdAt
    updatedAt
    __typename
  }
}` as GeneratedSubscription<
    OnCreateRecordBriefingSegmentSubscriptionVariables,
    OnCreateRecordBriefingSegmentSubscription
  >;

export const onUpdateRecordBriefingSegmentOptimized =
  /* GraphQL */ `subscription OnUpdateRecordBriefingSegment(
  $filter: ModelSubscriptionRecordBriefingSegmentFilterInput
) {
  onUpdateRecordBriefingSegment(filter: $filter) {
    id
    logicalSegmentID
    title
    text
    bodyParts
    editedBy
    pdfFileName
    sourceTag
    segmentNumber
    sourcePdfS3Key
    startPageNumber
    endPageNumber
    recordType
    extractedDate
    doctorName
    processingModel
    classificationConfidence
    hasDate
    potentialDuplicateID
    needsReview
    status
    createdAt
    updatedAt
    __typename
  }
}` as GeneratedSubscription<
    OnDeleteRecordBriefingSegmentSubscriptionVariables,
    OnDeleteRecordBriefingSegmentSubscription
  >;

export const onUpdateHistoryValidationOptimized =
  `subscription OnUpdateHistoryValidation(
        $filter: ModelSubscriptionHistoryValidationFilterInput
      ) {
        onUpdateHistoryValidation(filter: $filter) {
          id
          appointmentRecordID
          status
          validatedAt
          errorMessage
          groups {
            id
            title
            status
            issues {
              id
              title
              description
              patientInfoSection
              issueType
              __typename
            }
            __typename
          }
          createdAt
          updatedAt
          __typename
        }
      }
    ` as GeneratedSubscription<
    OnUpdateHistoryValidationSubscriptionVariables,
    OnUpdateHistoryValidationOptimizedSubscription
  >;

export const onCreateHistoryValidationOptimized =
  `subscription OnCreateHistoryValidation(
    $filter: ModelSubscriptionHistoryValidationFilterInput
  ) {
    onCreateHistoryValidation(filter: $filter) {
      id
      appointmentRecordID
      status
      validatedAt
      errorMessage
      groups {
        id
        title
        status
        issues {
          id
          title
          description
          patientInfoSection
          issueType
          __typename
        }
        __typename
      }
      createdAt
      updatedAt
      __typename
    }
  }
` as GeneratedSubscription<
    OnCreateHistoryValidationSubscriptionVariables,
    OnCreateHistoryValidationOptimizedSubscription
  >;

export const onUpdateValidationCheckOptimized =
  /* GraphQL */ `subscription OnUpdateValidationCheck(
    $filter: ModelSubscriptionValidationCheckFilterInput
  ) {
    onUpdateValidationCheck(filter: $filter) {
      id
      validationID
      checkType
      inputResourceURIs
      status
      issues {
        items {
          id
          validationID
          validationCheckID
          title
          issue
          status
          resourceURI
          issueGroup
          issueType
          createdAt
          updatedAt
          __typename
        }
        nextToken
        __typename
      }
      createdAt
      updatedAt
      __typename
    }
  }
` as GeneratedSubscription<
    OnUpdateValidationCheckSubscriptionVariables,
    OnUpdateValidationCheckOptimizedSubscription
  >;

export const onCreateValidationCheckOptimized =
  /* GraphQL */ `subscription OnCreateValidationCheck(
    $filter: ModelSubscriptionValidationCheckFilterInput
  ) {
    onCreateValidationCheck(filter: $filter) {
      id
      validationID
      checkType
      inputResourceURIs
      status
      issues {
        items {
          id
          validationID
          validationCheckID
          title
          issue
          status
          resourceURI
          issueGroup
          issueType
          createdAt
          updatedAt
          __typename
        }
        nextToken
        __typename
      }
      createdAt
      updatedAt
      __typename
    }
  }
` as GeneratedSubscription<
    OnCreateValidationCheckSubscriptionVariables,
    OnCreateValidationCheckOptimizedSubscription
  >;

export const onDeleteRecordBriefingSegmentOptimized = /* GraphQL */ `
  subscription OnDeleteRecordBriefingSegment(
    $filter: ModelSubscriptionRecordBriefingSegmentFilterInput
  ) {
    onDeleteRecordBriefingSegment(filter: $filter) {
      id
      logicalSegmentID
      createdAt
      updatedAt
      __typename
    }
  }
`;

// ========================================================================================
// Record Briefing Segment Mapping Subscriptions
// ========================================================================================

export const onUpdateRecordBriefingSegmentMappingOptimized = /* GraphQL */ `
  subscription OnUpdateRecordBriefingSegmentMapping(
    $filter: ModelSubscriptionRecordBriefingSegmentMappingFilterInput
  ) {
    onUpdateRecordBriefingSegmentMapping(filter: $filter) {
      id
      briefingID
      logicalSegmentID
      publishedSegmentID
      draftSegmentID
      markedForDeletion
      markedForCreation
      createdAt
      updatedAt
      __typename
    }
  }
` as GeneratedSubscription<
  OnUpdateRecordBriefingSegmentMappingSubscriptionVariables,
  OnUpdateRecordBriefingSegmentMappingSubscription
>;

// ========================================================================================
// Assessment Subscriptions
// ========================================================================================

/**
 * Assessment update subscription used for cross-tab sync.
 *
 * We intentionally keep the selection set small — the tab's parent
 * (`AppointmentDetails`) reacts to any fired event by invalidating/refetching
 * the full appointment, so we don't need a deep payload here.
 *
 * We do, however, include `assessmentMode` and `updatedAt` in addition to
 * `markedAsDone` so that:
 *   - AppSync projects meaningful data from the mutation response even when
 *     a backend lambda returns a narrow selection set (AppSync will null any
 *     field not present in the mutation result).
 *   - Consumers that need to detect "something changed" can compare
 *     `updatedAt` without a second round-trip.
 *   - Mode switches (STRUCTURED / FREEFORM / GOOGLE_DOC) are observable
 *     directly on the event if a future consumer wants to skip the refetch.
 */
export const onUpdateAssessmentOptimized = /* GraphQL */ `
  subscription OnUpdateAssessment(
    $filter: ModelSubscriptionAssessmentFilterInput
  ) {
    onUpdateAssessment(filter: $filter) {
      id
      markedAsDone
      assessmentMode
      updatedAt
      __typename
    }
  }
` as GeneratedSubscription<
  OnUpdateAssessmentSubscriptionVariables,
  OnUpdateAssessmentSubscription
>;

// ========================================================================================
// AppointmentFileReference Subscriptions
// ========================================================================================

export const onCreateAppointmentFileReferenceOptimized = /* GraphQL */ `
  subscription OnCreateAppointmentFileReference(
    $filter: ModelSubscriptionAppointmentFileReferenceFilterInput
  ) {
    onCreateAppointmentFileReference(filter: $filter) {
      id
      appointmentID
      uploadedFileID
      status
      createdAt
      updatedAt
      __typename
    }
  }
`;

export const onUpdateAppointmentFileReferenceOptimized = /* GraphQL */ `
  subscription OnUpdateAppointmentFileReference(
    $filter: ModelSubscriptionAppointmentFileReferenceFilterInput
  ) {
    onUpdateAppointmentFileReference(filter: $filter) {
      id
      appointmentID
      uploadedFileID
      status
      createdAt
      updatedAt
      __typename
    }
  }
`;

export const onDeleteAppointmentFileReferenceOptimized = /* GraphQL */ `
  subscription OnDeleteAppointmentFileReference(
    $filter: ModelSubscriptionAppointmentFileReferenceFilterInput
  ) {
    onDeleteAppointmentFileReference(filter: $filter) {
      id
      appointmentID
      uploadedFileID
      status
      createdAt
      updatedAt
      __typename
    }
  }
`;

// ========================================================================================
// Record Briefing Suggestion Generation Subscriptions
// ========================================================================================

/**
 * Optimized subscription for watching suggestionGeneration status changes on a RecordBriefing.
 * Only fetches the suggestionGeneration field to minimize payload size.
 */
export const onUpdateRecordBriefingSuggestionGenerationOptimized = /* GraphQL */ `
  subscription OnUpdateRecordBriefingSuggestionGeneration(
    $filter: ModelSubscriptionRecordBriefingFilterInput
  ) {
    onUpdateRecordBriefing(filter: $filter) {
      id
      suggestionGeneration {
        status
        jobId
        startedAt
        completedAt
        error
      }
    }
  }
`;

// ========================================================================================
// Concurrent Editing Awareness Subscriptions
// ========================================================================================

/**
 * Subscription for watching editing presence changes on a RecordBriefing.
 * Used to show who is currently editing the briefing.
 */
export const onUpdateRecordBriefingPresenceOptimized = /* GraphQL */ `
  subscription OnUpdateRecordBriefingPresence(
    $filter: ModelSubscriptionRecordBriefingFilterInput
  ) {
    onUpdateRecordBriefing(filter: $filter) {
      id
      editingPresence
    }
  }
`;

// ── AppointmentNotification ──

export const onCreateAppointmentNotificationOptimized = /* GraphQL */ `
  subscription OnCreateAppointmentNotification(
    $filter: ModelSubscriptionAppointmentNotificationFilterInput
  ) {
    onCreateAppointmentNotification(filter: $filter) {
      id
      appointmentRecordID
      notificationType
      title
      body
      status
      readAt
      createdAt
      updatedAt
    }
  }
`;

export const onUpdateAppointmentNotificationOptimized = /* GraphQL */ `
  subscription OnUpdateAppointmentNotification(
    $filter: ModelSubscriptionAppointmentNotificationFilterInput
  ) {
    onUpdateAppointmentNotification(filter: $filter) {
      id
      appointmentRecordID
      notificationType
      title
      body
      status
      readAt
      createdAt
      updatedAt
    }
  }
`;

// ========================================================================================
// Document Subscriptions (debug page)
// ========================================================================================

// The generated `onUpdateDocument` / `onCreateDocument` subscriptions strip
// `pages.items` at maxDepth=2 — the debug Record-Review graph reads
// `doc.pages?.items` to compute "pages with text/image" stats. Add a Debug
// variant that mirrors the matching `documentsByUploadedFileIDForDebug` query
// shape so subscription updates keep the cached entry whole.

export type OnUpdateDocumentForDebugSubscription = {
  onUpdateDocument?: DocumentForDebug | null;
};

export type OnCreateDocumentForDebugSubscription = {
  onCreateDocument?: DocumentForDebug | null;
};

const DOCUMENT_FOR_DEBUG_SELECTION = `
    id
    uploadedFileID
    pageCount
    executionArn
    segmentSplits
    createdAt
    updatedAt
    preview {
      bucket
      key
      __typename
    }
    pages {
      items {
        id
        pageNumber
        text
        imageS3Key
        __typename
      }
      nextToken
      __typename
    }
    __typename
`;

export const onUpdateDocumentForDebug =
  /* GraphQL */ `subscription OnUpdateDocument(
  $filter: ModelSubscriptionDocumentFilterInput
) {
  onUpdateDocument(filter: $filter) {
${DOCUMENT_FOR_DEBUG_SELECTION}  }
}
` as GeneratedSubscription<
    OnUpdateDocumentSubscriptionVariables,
    OnUpdateDocumentForDebugSubscription
  >;

export const onCreateDocumentForDebug =
  /* GraphQL */ `subscription OnCreateDocument(
  $filter: ModelSubscriptionDocumentFilterInput
) {
  onCreateDocument(filter: $filter) {
${DOCUMENT_FOR_DEBUG_SELECTION}  }
}
` as GeneratedSubscription<
    OnCreateDocumentSubscriptionVariables,
    OnCreateDocumentForDebugSubscription
  >;
